import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Bookmark,
  CalendarDays,
  Clock3,
  Plus,
} from "lucide-react";
import BookmarkShapeCard from "./GroupCard";
import type { BookmarkGroupData } from "../utils/bookmarkGroupData";
import {
  ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
  isElectronicBookmarkGroupNameTooLong,
} from "../api/editGropus";
import {
  ACTION_BUTTON_NEUTRAL_CLASS_NAME,
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getAdaptiveActionButtonStyle,
  getActionButtonStyle,
} from "../utils/actionButtonStyles";
import { blurActiveElement, changeBookNums } from "../utils/helpers";

type BookmarkMessage = {
  type: "success" | "error" | "info" | "warning";
  text: string;
} | null;

export type BookmarkCardRenderArgs = {
  bookmark: ElectronicBookmark;
  activeBookGroup: BookmarkGroupData;
  accentColor: string;
  isActive: boolean;
  badge: string;
  hoverLabel: string;
  footer: React.ReactNode;
  isBadgeMuted: boolean;
  onSelect: () => void;
};

type BookmarkGroupsBrowserProps = {
  preferredBookType?: "adults" | "kids" | null;
  activeGroupName: string;
  selectedBookId: string | null;
  bookmarkGroups: BookmarkGroupData[];
  onCreateGroup: (bookId: string, groupName: string) => Promise<void> | void;
  isCreatingGroup: boolean;
  onOpenSavedBookmark?: (bookmark: ElectronicBookmark) => void;
  isLoadingBookmarks: boolean;
  onClearMessage: () => void;
  bookmarkMessage: BookmarkMessage;
  showBookmarkIcon?: boolean;
  showBookmarkColumnAccentBackground?: boolean;
  showGroupHeading?: boolean;
  className?: string;
  renderBookmarkCard?: (args: BookmarkCardRenderArgs) => React.ReactNode;
  onVisibleBookChange?: (bookId: string | null) => void;
  createGroupControlsPlacement?: "top" | "bottom";
  createGroupMessagePlacement?: "below" | "inline";
  createGroupControlsRowClassName?: string;
  createGroupInputClassName?: string;
  actionButtonStyleVariant?: "default" | "admin" | "modal";
  bookTypeToggleStyleVariant?: "default" | "admin";
};

const normalizeGroupName = (groupName: string) =>
  groupName.trim().replace(/\s+/g, " ").toLowerCase();

const withHexAlpha = (color: string, alphaHex: string) =>
  /^#[\da-fA-F]{6}$/.test(color) ? `${color}${alphaHex}` : color;

const formatBookmarkDate = (timestamp: number) =>
  new Date(timestamp).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const formatBookmarkTime = (timestamp: number) =>
  new Date(timestamp).toLocaleString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
  });

const BookmarkGroupsBrowser: React.FC<BookmarkGroupsBrowserProps> = ({
  preferredBookType,
  activeGroupName,
  selectedBookId,
  bookmarkGroups,
  onCreateGroup,
  isCreatingGroup,
  onOpenSavedBookmark,
  isLoadingBookmarks,
  onClearMessage,
  bookmarkMessage,
  showBookmarkIcon = true,
  showBookmarkColumnAccentBackground = showBookmarkIcon,
  showGroupHeading = true,
  className,
  renderBookmarkCard,
  onVisibleBookChange,
  createGroupControlsPlacement = "top",
  createGroupMessagePlacement = "below",
  createGroupControlsRowClassName,
  createGroupInputClassName,
  actionButtonStyleVariant = "default",
  bookTypeToggleStyleVariant = "default",
}) => {
  const usesStyledActionButtons = actionButtonStyleVariant !== "default";
  const usesAdminPhoneBookSelector = actionButtonStyleVariant === "admin";
  const usesAdminBookTypeToggleStyle =
    bookTypeToggleStyleVariant === "admin" ||
    actionButtonStyleVariant === "admin";
  const adultBookmarkGroups = useMemo(
    () => bookmarkGroups.filter((group) => group.isAdultBook),
    [bookmarkGroups],
  );
  const kidsBookmarkGroups = useMemo(
    () => bookmarkGroups.filter((group) => !group.isAdultBook),
    [bookmarkGroups],
  );
  const selectedBookGroup = useMemo(
    () =>
      bookmarkGroups.find((group) => group.bookId === selectedBookId) ?? null,
    [bookmarkGroups, selectedBookId],
  );

  const [activeBookType, setActiveBookType] = useState<"adults" | "kids">(
    preferredBookType ??
      (selectedBookGroup?.isAdultBook === false ? "kids" : "adults"),
  );
  const [activeAdultBookId, setActiveAdultBookId] = useState<string | null>(
    selectedBookGroup?.isAdultBook ? selectedBookGroup.bookId : null,
  );
  const [activeKidsBookId, setActiveKidsBookId] = useState<string | null>(
    selectedBookGroup?.isAdultBook === false ? selectedBookGroup.bookId : null,
  );
  const [groupNameDraft, setGroupNameDraft] = useState("");
  const [localValidationMessage, setLocalValidationMessage] =
    useState<BookmarkMessage>(null);
  const [highlightedBookmark, setHighlightedBookmark] = useState<{
    bookId: string;
    groupNameNormalized: string;
  } | null>(null);
  const [hoveredBookId, setHoveredBookId] = useState<string | null>(null);
  const previousBookContextRef = useRef<string | null>(null);
  const onVisibleBookChangeRef = useRef(onVisibleBookChange);

  useEffect(() => {
    onVisibleBookChangeRef.current = onVisibleBookChange;
  }, [onVisibleBookChange]);

  useEffect(() => {
    const nextBookType =
      preferredBookType &&
      ((preferredBookType === "adults" && adultBookmarkGroups.length > 0) ||
        (preferredBookType === "kids" && kidsBookmarkGroups.length > 0))
        ? preferredBookType
        : selectedBookGroup
          ? selectedBookGroup.isAdultBook
            ? "adults"
            : "kids"
          : adultBookmarkGroups.length > 0
            ? "adults"
            : kidsBookmarkGroups.length > 0
              ? "kids"
              : "adults";

    setActiveBookType(nextBookType);
  }, [
    preferredBookType,
    selectedBookGroup,
    adultBookmarkGroups.length,
    kidsBookmarkGroups.length,
  ]);

  useEffect(() => {
    setActiveAdultBookId((currentBookId) => {
      if (
        currentBookId &&
        adultBookmarkGroups.some((group) => group.bookId === currentBookId)
      ) {
        return currentBookId;
      }

      if (selectedBookGroup?.isAdultBook) {
        return selectedBookGroup.bookId;
      }

      return adultBookmarkGroups[0]?.bookId ?? null;
    });
  }, [selectedBookGroup, adultBookmarkGroups]);

  useEffect(() => {
    setActiveKidsBookId((currentBookId) => {
      if (
        currentBookId &&
        kidsBookmarkGroups.some((group) => group.bookId === currentBookId)
      ) {
        return currentBookId;
      }

      if (selectedBookGroup?.isAdultBook === false) {
        return selectedBookGroup.bookId;
      }

      return kidsBookmarkGroups[0]?.bookId ?? null;
    });
  }, [selectedBookGroup, kidsBookmarkGroups]);

  useEffect(() => {
    const currentBookContext = `${activeBookType}:${activeAdultBookId ?? ""}:${
      activeKidsBookId ?? ""
    }`;
    const isFirstVisibleContext = previousBookContextRef.current === null;
    const didBookContextChange =
      !isFirstVisibleContext &&
      previousBookContextRef.current !== currentBookContext;

    if (isFirstVisibleContext || didBookContextChange) {
      setGroupNameDraft("");
      setLocalValidationMessage(null);
    }
    previousBookContextRef.current = currentBookContext;
  }, [activeBookType, activeAdultBookId, activeKidsBookId]);

  const activeBookTabs =
    activeBookType === "kids" ? kidsBookmarkGroups : adultBookmarkGroups;
  const activeBookId =
    activeBookType === "kids" ? activeKidsBookId : activeAdultBookId;
  const activeBookGroup =
    activeBookTabs.find((group) => group.bookId === activeBookId) ??
    activeBookTabs[0] ??
    null;
  const activeBookColor = activeBookGroup?.primaryColor ?? "#f6851f";
  const activeVisibleBookId = activeBookGroup?.bookId ?? null;
  const activeGroupHighlight =
    highlightedBookmark ??
    (selectedBookId &&
    activeBookGroup?.bookId === selectedBookId &&
    activeGroupName.trim()
      ? {
          bookId: selectedBookId,
          groupNameNormalized: normalizeGroupName(activeGroupName),
        }
      : null);

  useEffect(() => {
    onVisibleBookChangeRef.current?.(activeVisibleBookId);
  }, [activeVisibleBookId]);

  const clearWarningMessage = () => {
    if (bookmarkMessage?.type === "warning") {
      onClearMessage();
    }
  };

  const isGroupNameTooLong =
    isElectronicBookmarkGroupNameTooLong(groupNameDraft);
  const displayedMessage = localValidationMessage ?? bookmarkMessage;
  const renderedCreateGroupMessage = displayedMessage ? (
    <div
      className={`rounded-2xl border px-4 py-3 text-sm ${
        displayedMessage.type === "error"
          ? "border-red-200 bg-red-50 text-red-700"
          : displayedMessage.type === "success"
            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
            : displayedMessage.type === "warning"
              ? "border-amber-300 bg-amber-50 text-amber-900 shadow-[0_12px_30px_rgba(245,158,11,0.12)]"
              : "border-slate-200 bg-slate-100 text-slate-700"
      }`}
    >
      <div className="flex items-start gap-3">
        {displayedMessage.type === "warning" ? (
          <AlertTriangle
            size={18}
            strokeWidth={2.3}
            className="mt-0.5 shrink-0 text-amber-600"
            aria-hidden="true"
          />
        ) : null}
        <span>{displayedMessage.text}</span>
      </div>
    </div>
  ) : null;

  const handleCreateGroup = async () => {
    const cleanedGroupName = groupNameDraft.trim().replace(/\s+/g, " ");
    if (!activeBookGroup?.bookId || !cleanedGroupName) return;
    if (isGroupNameTooLong) {
      setLocalValidationMessage({
        type: "error",
        text: ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
      });
      return;
    }

    try {
      await onCreateGroup(activeBookGroup.bookId, cleanedGroupName);
      blurActiveElement();
      setHighlightedBookmark({
        bookId: activeBookGroup.bookId,
        groupNameNormalized: normalizeGroupName(cleanedGroupName),
      });
      setGroupNameDraft("");
      setLocalValidationMessage(null);
    } catch {
      return;
    }
  };

  const handleOpenBookmark = (bookmark: ElectronicBookmark) => {
    setHighlightedBookmark({
      bookId: bookmark.bookId,
      groupNameNormalized: normalizeGroupName(bookmark.groupName),
    });
    onOpenSavedBookmark?.(bookmark);
  };

  const createGroupControls = (
    <div className={createGroupControlsPlacement === "top" ? "mb-4" : "mt-4"}>
      {createGroupControlsPlacement === "top" ? (
        <div className="border-b border-slate-200/80 pb-4">
          <div
            className={`${
              createGroupMessagePlacement === "inline"
                ? "flex flex-col gap-3 lg:flex-row lg:items-start"
                : ""
            }`}
          >
            <div
              className={`flex w-full items-center gap-3 rounded-[22px] ${
                createGroupControlsRowClassName ?? ""
              }`}
            >
              <input
                type="text"
                value={groupNameDraft}
                aria-label="Nazwa grupy"
                onChange={(e) => {
                  setGroupNameDraft(e.target.value);
                  setLocalValidationMessage(null);
                  if (bookmarkMessage?.type === "warning") {
                    onClearMessage();
                  }
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();

                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleCreateGroup();
                  }
                }}
                placeholder="Nadaj nazwę, by utworzyć grupę"
                className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-base outline-none transition placeholder:text-[13px] focus:border-[#f6851f] focus:ring-4 focus:ring-[#f6851f]/10 sm:text-sm sm:placeholder:text-sm ${
                  isGroupNameTooLong ? "text-red-600" : "text-slate-900"
                } ${createGroupInputClassName ?? ""}`}
              />
              <button
                type="button"
                onClick={() => void handleCreateGroup()}
                disabled={
                  isCreatingGroup ||
                  !groupNameDraft.trim() ||
                  !activeBookGroup?.bookId
                }
                className={`flex ${
                  usesStyledActionButtons
                    ? "h-12 w-12 rounded-full"
                    : "h-11 w-11 rounded-2xl"
                } shrink-0 items-center justify-center transition ${
                  usesStyledActionButtons
                    ? ACTION_BUTTON_TEXT_CLASS_NAME
                    : "text-white disabled:cursor-not-allowed disabled:opacity-40"
                }`}
                style={
                  actionButtonStyleVariant === "admin"
                    ? getActionButtonStyle("orange")
                    : actionButtonStyleVariant === "modal"
                      ? getAdaptiveActionButtonStyle(activeBookColor)
                      : { backgroundColor: activeBookColor }
                }
                aria-label="Dodaj grupę"
              >
                <Plus size={18} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </div>

            {createGroupMessagePlacement === "inline" &&
            renderedCreateGroupMessage ? (
              <div className="min-w-0 flex-1">{renderedCreateGroupMessage}</div>
            ) : null}
          </div>
        </div>
      ) : null}

      {displayedMessage && createGroupMessagePlacement === "below" && (
        <div
          className={`${
            createGroupControlsPlacement === "top" ? "mt-3" : "mb-3"
          }`}
        >
          {renderedCreateGroupMessage}
        </div>
      )}

      {createGroupControlsPlacement === "bottom" ? (
        <div className="border-t border-slate-200/80 pt-4">
          <div
            className={`flex w-full items-center gap-3 rounded-[22px] ${
              createGroupControlsRowClassName ?? ""
            }`}
          >
            <input
              type="text"
              value={groupNameDraft}
              aria-label="Nazwa grupy"
              onChange={(e) => {
                setGroupNameDraft(e.target.value);
                setLocalValidationMessage(null);
                if (bookmarkMessage?.type === "warning") {
                  onClearMessage();
                }
              }}
              onKeyDown={(e) => {
                e.stopPropagation();

                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleCreateGroup();
                }
              }}
              placeholder="Nadaj nazwę, by utworzyć grupę"
              className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-base outline-none transition placeholder:text-[13px] focus:border-[#f6851f] focus:ring-4 focus:ring-[#f6851f]/10 sm:text-sm sm:placeholder:text-sm ${
                isGroupNameTooLong ? "text-red-600" : "text-slate-900"
              } ${createGroupInputClassName ?? ""}`}
            />
            <button
              type="button"
              onClick={() => void handleCreateGroup()}
              disabled={
                isCreatingGroup ||
                !groupNameDraft.trim() ||
                !activeBookGroup?.bookId
              }
              className={`flex ${
                usesStyledActionButtons
                  ? "h-12 w-12 rounded-full"
                  : "h-11 w-11 rounded-2xl"
              } shrink-0 items-center justify-center transition ${
                usesStyledActionButtons
                  ? ACTION_BUTTON_TEXT_CLASS_NAME
                  : "text-white disabled:cursor-not-allowed disabled:opacity-40"
              }`}
              style={
                actionButtonStyleVariant === "admin"
                  ? getActionButtonStyle("orange")
                  : actionButtonStyleVariant === "modal"
                    ? getAdaptiveActionButtonStyle(activeBookColor)
                    : { backgroundColor: activeBookColor }
              }
              aria-label="Dodaj grupę"
            >
              <Plus size={18} strokeWidth={2.5} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className={`grid h-full min-h-0 flex-1 gap-0 lg:grid-cols-[290px,minmax(0,1fr)] ${className ?? ""}`}
    >
      <div
        className={`flex h-full min-h-0 flex-col border-b border-slate-200/80 px-5 py-5 sm:px-7 lg:border-b-0 lg:border-r lg:px-6 lg:py-6 ${
          usesAdminPhoneBookSelector
            ? "max-h-none overflow-visible sm:max-h-[42vh] sm:overflow-hidden lg:max-h-none"
            : "max-h-[38vh] overflow-hidden sm:max-h-[42vh] lg:max-h-none"
        } ${
          showBookmarkColumnAccentBackground
            ? "bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_42%),linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,248,240,0.9))]"
            : ""
        }`}
      >
        <div
          className={
            showBookmarkIcon ? "flex items-center gap-3" : "flex w-full"
          }
        >
          {showBookmarkIcon ? (
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] border border-white/60 bg-white/80 shadow-[0_18px_40px_rgba(148,163,184,0.16)]"
              style={{ color: activeBookColor }}
            >
              <Bookmark size={24} strokeWidth={2.1} aria-hidden="true" />
            </div>
          ) : null}

          <div
            className={`${
              showBookmarkIcon ? "flex-1" : "w-full"
            } grid grid-cols-2 ${
              usesAdminBookTypeToggleStyle
                ? "gap-2"
                : "rounded-[18px] border border-slate-200/80 bg-white/85 p-1 shadow-sm"
            }`}
          >
            <button
              type="button"
              onClick={() => {
                clearWarningMessage();
                setActiveBookType("adults");
              }}
              disabled={adultBookmarkGroups.length === 0}
              aria-pressed={activeBookType === "adults"}
              className={`w-full ${
                usesAdminBookTypeToggleStyle ? "rounded-full" : "rounded-[14px]"
              } px-4 py-3 text-sm font-semibold transition ${
                usesAdminBookTypeToggleStyle
                  ? activeBookType === "adults"
                    ? ACTION_BUTTON_TEXT_CLASS_NAME
                    : ACTION_BUTTON_NEUTRAL_CLASS_NAME
                  : activeBookType === "adults"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
              } disabled:cursor-not-allowed disabled:opacity-40`}
              style={
                usesAdminBookTypeToggleStyle && activeBookType === "adults"
                  ? getActionButtonStyle("orange")
                  : undefined
              }
            >
              Adults
            </button>

            <button
              type="button"
              onClick={() => {
                clearWarningMessage();
                setActiveBookType("kids");
              }}
              disabled={kidsBookmarkGroups.length === 0}
              aria-pressed={activeBookType === "kids"}
              className={`w-full ${
                usesAdminBookTypeToggleStyle ? "rounded-full" : "rounded-[14px]"
              } px-4 py-3 text-sm font-semibold transition ${
                usesAdminBookTypeToggleStyle
                  ? activeBookType === "kids"
                    ? ACTION_BUTTON_TEXT_CLASS_NAME
                    : ACTION_BUTTON_NEUTRAL_CLASS_NAME
                  : activeBookType === "kids"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
              } disabled:cursor-not-allowed disabled:opacity-40`}
              style={
                usesAdminBookTypeToggleStyle && activeBookType === "kids"
                  ? getActionButtonStyle("sky")
                  : undefined
              }
            >
              Kids
            </button>
          </div>
        </div>

        <div
          className={`mt-5 min-h-0 lg:mt-6 ${
            usesAdminPhoneBookSelector ? "sm:flex-1" : "flex-1"
          }`}
        >
          {activeBookTabs.length === 0 ? (
            <div className="rounded-[24px] border border-dashed border-white/70 bg-white/60 px-4 py-6 text-sm text-slate-500">
              Brak podręczników w tej sekcji.
            </div>
          ) : (
            <div
              className={`grid gap-3 ${
                usesAdminPhoneBookSelector
                  ? "grid-cols-3 gap-2 overflow-visible pr-0 sm:max-h-full sm:grid-cols-2 sm:gap-3 sm:overflow-y-auto sm:pr-1 lg:grid-cols-1"
                  : "max-h-full grid-cols-1 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-1"
              }`}
            >
              {activeBookTabs.map((group) => {
                const isActive = group.bookId === activeBookGroup?.bookId;
                const isCompactAdultTab = group.isAdultBook;
                const useKidsSubtitleOnPhone =
                  usesAdminPhoneBookSelector &&
                  !group.isAdultBook &&
                  Boolean(group.bookSubtitle);
                const isHovered = hoveredBookId === group.bookId;
                const hoverColor = withHexAlpha(
                  group.primaryColor ?? "#f6851f",
                  "1F",
                );

                return (
                  <button
                    key={group.bookId}
                    type="button"
                    onClick={() => {
                      clearWarningMessage();
                      if (group.isAdultBook) {
                        setActiveAdultBookId(group.bookId);
                        return;
                      }

                      setActiveKidsBookId(group.bookId);
                    }}
                    onMouseEnter={() => setHoveredBookId(group.bookId)}
                    onMouseLeave={() => setHoveredBookId(null)}
                    onFocus={() => setHoveredBookId(group.bookId)}
                    onBlur={() => setHoveredBookId(null)}
                    aria-label={group.bookTitle}
                    aria-pressed={isActive}
                    className="h-full w-full overflow-hidden rounded-[18px] transition-colors duration-200"
                    style={{
                      backgroundColor:
                        isActive || isHovered ? hoverColor : undefined,
                    }}
                  >
                    <div
                      className={`flex gap-3 px-3 ${
                        usesAdminPhoneBookSelector
                          ? "h-full flex-col items-center px-2 py-2.5 text-center sm:flex-row sm:items-center sm:px-3 sm:py-3 sm:text-left"
                          : `items-center ${isCompactAdultTab ? "py-2" : "py-3"}`
                      }`}
                    >
                      <div
                        className={`shrink-0 overflow-hidden rounded-2xl ${
                          usesAdminPhoneBookSelector
                            ? "aspect-square w-14 sm:h-16 sm:w-16"
                            : "h-16 w-16"
                        }`}
                      >
                        {group.bookCoverSrc ? (
                          <img
                            src={group.bookCoverSrc}
                            alt={group.bookTitle}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-slate-200 px-2 text-center text-xs font-semibold text-slate-500">
                            {group.bookTitle.replace("English Unlocked ", "")}
                          </div>
                        )}
                      </div>

                      <div
                        className={`min-w-0 ${
                          usesAdminPhoneBookSelector
                            ? "w-full text-center sm:pr-1 sm:text-left"
                            : "pr-1 text-left"
                        }`}
                      >
                        {useKidsSubtitleOnPhone ? (
                          <>
                            <p className="text-[11px] leading-4 tracking-[-0.02em] text-slate-900 sm:hidden">
                              {group.bookSubtitle}
                            </p>
                            <p className="hidden text-sm font-normal leading-5 tracking-[-0.02em] text-slate-900 sm:block">
                              {group.bookTitle}
                            </p>
                          </>
                        ) : (
                          <p
                            className={`tracking-[-0.02em] text-slate-900 ${
                              usesAdminPhoneBookSelector
                                ? "text-[11px] font-semibold leading-4 sm:text-sm sm:font-normal sm:leading-5"
                                : "text-sm leading-5"
                            }`}
                          >
                            {group.bookTitle}
                          </p>
                        )}
                        {group.bookSubtitle ? (
                          <p
                            className={`mt-1 text-slate-500 ${
                              usesAdminPhoneBookSelector
                                ? "hidden text-[11px] italic leading-4 sm:block sm:text-sm sm:leading-5"
                                : "text-sm italic leading-5"
                            }`}
                          >
                            {group.bookSubtitle}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div
        className={`min-h-0 flex flex-1 flex-col ${
          showGroupHeading
            ? "p-5 sm:p-6 lg:px-8 lg:py-8"
            : "px-5 py-5 sm:px-7 sm:py-5 lg:px-6 lg:py-6"
        }`}
      >
        {showGroupHeading ? (
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">
            Wybierz grupę
          </p>
        ) : null}

        {createGroupControlsPlacement === "top" ? createGroupControls : null}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoadingBookmarks ? (
            <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/80 px-5 py-8 text-center text-sm text-slate-500">
              Ładowanie zakładek...
            </div>
          ) : !activeBookGroup ? (
            <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/80 px-5 py-8 text-center text-sm text-slate-500">
              Brak podręcznika do wyświetlenia.
            </div>
          ) : activeBookGroup.bookmarks.length === 0 ? (
            <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/80 px-5 py-8 text-center text-sm text-slate-500">
              Brak zapisanych zakładek dla wybranego podręcznika.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 pr-1 sm:grid-cols-2 xl:grid-cols-3">
              {activeBookGroup.bookmarks.map((bookmark) => {
                const savedPageNum =
                  typeof bookmark.pageNum === "number"
                    ? bookmark.pageNum
                    : null;
                const hasSavedBookmark = savedPageNum !== null;
                const pageLabel = hasSavedBookmark
                  ? activeBookGroup.isAdultBook
                    ? changeBookNums(savedPageNum)
                    : savedPageNum
                  : null;
                const badge = `strona ${pageLabel ?? 1}`;
                const isActive =
                  activeGroupHighlight?.bookId === activeBookGroup.bookId &&
                  normalizeGroupName(bookmark.groupName) ===
                    activeGroupHighlight.groupNameNormalized;
                const footer = hasSavedBookmark ? (
                  <div className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays
                        size={14}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                      <span>{formatBookmarkDate(bookmark.updatedAt)}</span>
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1.5 text-right">
                      <Clock3 size={14} strokeWidth={2} aria-hidden="true" />
                      <span>{formatBookmarkTime(bookmark.updatedAt)}</span>
                    </span>
                  </div>
                ) : (
                  <div className="text-slate-500">Nie dodano zakładki</div>
                );
                const onSelect = () => handleOpenBookmark(bookmark);
                const cardArgs: BookmarkCardRenderArgs = {
                  bookmark,
                  activeBookGroup,
                  accentColor: activeBookColor,
                  isActive,
                  badge,
                  hoverLabel: `${pageLabel ?? 1}`,
                  footer,
                  isBadgeMuted: !hasSavedBookmark,
                  onSelect,
                };

                return (
                  <React.Fragment key={bookmark.id}>
                    {renderBookmarkCard ? (
                      renderBookmarkCard(cardArgs)
                    ) : (
                      <BookmarkShapeCard
                        title={bookmark.groupName}
                        badge={badge}
                        accentColor={activeBookColor}
                        isBadgeMuted={!hasSavedBookmark}
                        hoverLabel={`${pageLabel ?? 1}`}
                        footer={footer}
                        isActive={isActive}
                        onClick={onSelect}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {createGroupControlsPlacement === "bottom" ? createGroupControls : null}
      </div>
    </div>
  );
};

export default BookmarkGroupsBrowser;
